import React, { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  Clock, Calendar, ThumbsUp, ThumbsDown, ArrowRight, ArrowLeft,
  MessageCircle, Workflow, BookOpenCheck,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSeo, organizationSchema, breadcrumbSchema, faqSchema } from "../lib/useSeo";
import { ARTICLES, CATEGORIES, getArticle } from "../lib/articles";
import { whatsappLink } from "../lib/whatsapp";

const fmtDate = (iso, locale) =>
  new Date(iso).toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
    year: "numeric", month: "long", day: "numeric",
  });

const Block = ({ block }) => {
  if (block.type === "h2") {
    return <h2 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight mt-12 mb-4 scroll-mt-24">{block.text}</h2>;
  }
  if (block.type === "h3") {
    return <h3 className="font-display font-semibold text-xl text-white mt-8 mb-3">{block.text}</h3>;
  }
  if (block.type === "p") {
    return <p className="text-white/75 leading-[1.75] text-[16.5px] mb-5">{block.text}</p>;
  }
  if (block.type === "list") {
    return (
      <ul className="my-6 space-y-3">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-white/80 text-[16px] leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-orange shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "diagram") {
    return (
      <div className="my-10 glass rounded-3xl p-8 md:p-10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-orange/15 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-brand-orange/15 border border-brand-orange/30 flex items-center justify-center shrink-0">
            <Workflow className="w-5 h-5 text-brand-orange" />
          </div>
          <div>
            <div className="label-mono text-brand-orange mb-1.5">Diagram</div>
            <div className="font-display font-bold text-xl md:text-2xl text-white tracking-tight">{block.title}</div>
            {block.subtitle && <div className="mt-2 text-white/55 text-sm">{block.subtitle}</div>}
          </div>
        </div>
        {/* Decorative SVG node-line illustration */}
        <svg viewBox="0 0 600 140" className="mt-6 w-full h-32 opacity-80" aria-hidden="true">
          <defs>
            <linearGradient id="dg-line" x1="0" x2="1">
              <stop offset="0%" stopColor="#FF6A00" stopOpacity="0.0" />
              <stop offset="50%" stopColor="#FF6A00" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#FF6A00" stopOpacity="0.0" />
            </linearGradient>
          </defs>
          <line x1="40" y1="70" x2="560" y2="70" stroke="url(#dg-line)" strokeWidth="1.5" />
          {[80, 220, 360, 500].map((x, i) => (
            <g key={i}>
              <circle cx={x} cy="70" r="22" fill="#0F1119" stroke="#FF6A00" strokeOpacity="0.6" />
              <circle cx={x} cy="70" r="6" fill="#FF6A00" />
            </g>
          ))}
        </svg>
      </div>
    );
  }
  if (block.type === "mini-case") {
    return (
      <div className="my-10 glass-strong rounded-3xl p-7 md:p-9 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-orange/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-5">
            <BookOpenCheck className="w-5 h-5 text-brand-orange" />
            <span className="label-mono">Mini Case</span>
          </div>
          <dl className="grid md:grid-cols-3 gap-6 text-sm">
            {[
              { k: "Challenge", v: block.challenge },
              { k: "Solution", v: block.solution },
              { k: "Result", v: block.result },
            ].map((d, i) => (
              <div key={i}>
                <dt className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/45 mb-2">{d.k}</dt>
                <dd className="text-white/85 leading-relaxed">{d.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    );
  }
  return null;
};

const Feedback = ({ slug, locale }) => {
  const [state, setState] = useState({ up: 0, down: 0, voted: null });
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`art-helpful-${slug}`);
      const data = raw ? JSON.parse(raw) : { up: 0, down: 0 };
      const voted = localStorage.getItem(`art-helpful-vote-${slug}`);
      setState({ ...data, voted });
    } catch (err) { console.warn("Feedback: failed to read stored votes", err); }
  }, [slug]);
  const vote = (kind) => {
    if (state.voted) return;
    const next = { ...state, [kind]: (state[kind] || 0) + 1, voted: kind };
    setState(next);
    try {
      localStorage.setItem(`art-helpful-${slug}`, JSON.stringify({ up: next.up, down: next.down }));
      localStorage.setItem(`art-helpful-vote-${slug}`, kind);
    } catch (err) { console.warn("Feedback: failed to persist vote", err); }
  };
  const isDE = locale === "de";
  return (
    <div data-testid="article-feedback" className="my-12 glass rounded-3xl p-7 md:p-9 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
      <div>
        <div className="label-mono mb-2">{isDE ? "Feedback" : "Feedback"}</div>
        <div className="font-display font-semibold text-lg text-white">{isDE ? "War dieser Artikel hilfreich?" : "Was this article helpful?"}</div>
        {(state.up + state.down) > 0 && (
          <div className="mt-2 text-xs text-white/55">
            {isDE
              ? `${state.up} Leser:innen fanden diesen Artikel hilfreich`
              : `${state.up} readers found this article helpful`}
          </div>
        )}
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => vote("up")}
          disabled={!!state.voted}
          data-testid="feedback-up"
          className={`btn-secondary !px-5 ${state.voted === "up" ? "!border-brand-orange !text-brand-orange" : ""}`}
        >
          <ThumbsUp className="w-4 h-4" /> {isDE ? "Ja" : "Yes"}
        </button>
        <button
          onClick={() => vote("down")}
          disabled={!!state.voted}
          data-testid="feedback-down"
          className={`btn-secondary !px-5 ${state.voted === "down" ? "!border-white !text-white" : ""}`}
        >
          <ThumbsDown className="w-4 h-4" /> {isDE ? "Nein" : "No"}
        </button>
      </div>
    </div>
  );
};

export default function BlogArticlePage() {
  const { category, slug } = useParams();
  const { locale } = useI18n();
  const isDE = locale === "de";
  const article = getArticle(slug);
  const cat = CATEGORIES.find((c) => c.slug === category);

  const pageUrl = `https://www.joshifusiontech.de/blog/${category}/${slug}`;

  const articleSchema = article && cat && {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title[locale],
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    author: { "@type": "Person", name: article.author },
    publisher: { "@id": "https://www.joshifusiontech.de/#organization" },
    mainEntityOfPage: pageUrl,
    inLanguage: locale === "de" ? "de-DE" : "en-US",
    description: article.excerpt[locale],
    articleSection: cat.label[locale],
  };

  useSeo({
    title: article ? `${article.title[locale]} | Joshi FusionTech Media` : "Blog | Joshi FusionTech Media",
    description: article ? article.excerpt[locale] : undefined,
    canonical: pageUrl,
    locale,
    jsonLd: article && cat
      ? [
          organizationSchema(),
          articleSchema,
          breadcrumbSchema([
            { name: isDE ? "Start" : "Home", url: "https://www.joshifusiontech.de/" },
            { name: "Blog", url: "https://www.joshifusiontech.de/blog" },
            { name: cat.label[locale], url: `https://www.joshifusiontech.de/blog/${category}` },
            { name: article.title[locale], url: pageUrl },
          ]),
          faqSchema(article.faq[locale]),
        ]
      : null,
  });

  if (!article || !cat || article.category !== category) {
    return <Navigate to="/blog" replace />;
  }

  const related = ARTICLES.filter((a) => a.slug !== slug && a.category === category).slice(0, 3);
  if (related.length < 3) {
    ARTICLES.forEach((a) => {
      if (a.slug !== slug && !related.includes(a) && related.length < 3) related.push(a);
    });
  }

  return (
    <main data-testid={`blog-article-${slug}`} className="pt-32 pb-24">
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-24 mb-12">
        <div className="max-w-3xl mx-auto">
          <nav aria-label="Breadcrumb" className="mb-7">
            <ol className="flex items-center gap-2 text-xs font-mono text-white/50 flex-wrap">
              <li><Link to="/blog" className="hover:text-brand-orange">Blog</Link></li>
              <li className="text-white/30">/</li>
              <li><Link to={`/blog/${category}`} className="hover:text-brand-orange">{cat.label[locale]}</Link></li>
            </ol>
          </nav>
          <div className="label-mono text-brand-orange mb-4">{cat.label[locale]}</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl lg:text-[44px] tracking-tight text-white leading-[1.08]">
            {article.title[locale]}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/55">
            <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4" />{fmtDate(article.publishedAt, locale)}</span>
            <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" />{article.readMinutes} min</span>
            <span>· {article.author}</span>
            {article.updatedAt && article.updatedAt !== article.publishedAt && (
              <span className="text-white/40">{isDE ? `Aktualisiert: ${fmtDate(article.updatedAt, locale)}` : `Updated: ${fmtDate(article.updatedAt, locale)}`}</span>
            )}
          </div>
        </div>
      </section>

      {/* Cover */}
      <section className="px-6 md:px-12 lg:px-24 mb-14">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl h-56 md:h-96 relative overflow-hidden border border-brand-orange/20 shadow-[0_30px_100px_rgba(255,106,0,0.15)]">
            <img
              src={`/blog/${article.slug}.webp`}
              alt={article.title[locale]}
              loading="eager"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-8">{article.excerpt[locale]}</p>
          {article.blocks[locale].map((b, i) => (
            <Block key={i} block={b} />
          ))}

          {/* FAQ */}
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight mt-14 mb-5">FAQ</h2>
          <div className="space-y-3">
            {article.faq[locale].map((f, i) => (
              <details key={i} className="glass rounded-2xl p-5 group" data-testid={`article-faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="font-display font-semibold text-white">{f.q}</span>
                  <span className="text-brand-orange text-xl select-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-white/70 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>

          {/* Feedback */}
          <Feedback slug={slug} locale={locale} />

          {/* Author box */}
          <div className="my-12 glass rounded-3xl p-7 flex items-start gap-5">
            <div className="w-14 h-14 rounded-full bg-brand-orange/20 border border-brand-orange/40 flex items-center justify-center text-brand-orange font-display font-bold text-lg shrink-0">RJ</div>
            <div>
              <div className="font-display font-semibold text-white">{article.author}</div>
              <div className="text-sm text-white/55">{isDE ? "Inhaber · Joshi FusionTech Media" : "Founder · Joshi FusionTech Media"}</div>
              <p className="mt-2 text-sm text-white/65 leading-relaxed">
                {isDE
                  ? "Rudrik begleitet Unternehmen aus Berlin und der EU bei digitaler Transformation, AI-Automatisierung und nachhaltigem Wachstum."
                  : "Rudrik helps companies from Berlin and across the EU with digital transformation, AI automation and sustainable growth."}
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Related articles */}
      <section className="px-6 md:px-12 lg:px-24 mt-16">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight mb-6">
            {isDE ? "Verwandte Artikel" : "Related articles"}
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/blog/${r.category}/${r.slug}`}
                className="glass rounded-2xl p-5 hover:border-brand-orange/40 transition-all"
                data-testid={`related-article-${r.slug}`}
              >
                <div className="label-mono text-brand-orange mb-2">
                  {CATEGORIES.find((c) => c.slug === r.category)?.label[locale]}
                </div>
                <div className="font-display font-semibold text-white mb-2 leading-snug">{r.title[locale]}</div>
                <div className="text-xs text-white/45">{r.readMinutes} min · {fmtDate(r.publishedAt, locale)}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Related service + CTA */}
      <section className="px-6 md:px-12 lg:px-24 mt-16">
        <div className="max-w-5xl mx-auto glass-strong rounded-3xl p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-brand-orange/15 blur-3xl pointer-events-none" />
          <div className="relative grid lg:grid-cols-[1.3fr_1fr] gap-8 items-center">
            <div>
              <div className="label-mono mb-3">{isDE ? "Verwandte Leistung" : "Related service"}</div>
              <h2 className="font-display font-bold text-2xl md:text-3xl lg:text-4xl text-white tracking-tight leading-tight">
                {isDE ? "Brauchst du Unterstützung in diesem Bereich?" : "Need support in this area?"}
              </h2>
              <p className="mt-4 text-white/65 leading-relaxed">
                {isDE
                  ? "30-minütiges Strategiegespräch, kostenlos und unverbindlich. Wir hören zu und geben konkrete nächste Schritte."
                  : "30-minute strategy call, free and non-binding. We listen and give concrete next steps."}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Link to={`/services/${article.relatedService}`} data-testid="article-related-service" className="btn-primary !justify-center">
                {isDE ? "Mehr zur Leistung" : "About the service"} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/book" className="btn-secondary !justify-center">
                {isDE ? "Strategiegespräch buchen" : "Book a strategy call"}
              </Link>
              <a
                href={whatsappLink(isDE ? "Hallo, ich habe eine Frage." : "Hi, I have a question.")}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary !justify-center !border-[#25D366]/40"
              >
                <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 mt-8">
        <div className="max-w-5xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 label-mono text-white/55 hover:text-brand-orange transition-colors">
            <ArrowLeft className="w-4 h-4" /> {isDE ? "Zurück zum Blog" : "Back to blog"}
          </Link>
        </div>
      </section>
    </main>
  );
}
